import React from 'react';
import { InputAdornment, TextField } from '@mui/material';
import {
	UnitField,
	type UnitFieldProps,
	CurrencyField,
} from '@innovyze/stylovyze';
import {
	isSystemOfMeasurementUnit,
	isCurrencyUnit,
} from '@Utils/units/unitClassification';

type UnitAwareFieldProps = UnitFieldProps;

/**
 * UnitAwareField - Intelligently renders the appropriate unit field component
 *
 * This component automatically chooses between:
 * - CurrencyField: For currency units with special formatting and conversion ($)
 * - UnitField: For system-of-measurement units that need metric ↔ imperial conversion (mm, psi, °C, etc.)
 * - TextField with unit adornment: For universal scientific units that don't need conversion (kW, Hz, m³/s, etc.)
 * - TextField: Fallback when no unit is provided
 *
 * The decision is made based on unit classification using isCurrencyUnit() and isSystemOfMeasurementUnit().
 */
export const UnitAwareField: React.FC<UnitAwareFieldProps> = ({
	unit,
	defaultValue,
	onChange,
	onBlur,
	min,
	max,
	...otherProps
}) => {
	// No unit provided - use basic TextField
	if (!unit) {
		return (
			<TextField
				{...otherProps}
				type="number"
				fullWidth
				variant="outlined"
				inputProps={{
					inputMode: 'numeric',
					pattern: '[0-9]*',
				}}
				value={otherProps.value}
				onChange={onChange}
			/>
		);
	}

	// Currency units - use CurrencyField with special formatting
	if (isCurrencyUnit(unit)) {
		return (
			<CurrencyField
				{...otherProps}
				fullWidth
				variant="outlined"
				inputProps={{
					inputMode: 'numeric',
					pattern: '[0-9]*',
				}}
				defaultValue={defaultValue}
				onChange={onChange}
				onBlur={onBlur}
				min={min}
				max={max}
				step="any"
			/>
		);
	}

	// Determine appropriate component based on unit classification
	if (isSystemOfMeasurementUnit(unit)) {
		// Units requiring metric ↔ imperial conversion (mm, psi, degrees, etc.)
		return (
			<UnitField
				{...otherProps}
				fullWidth
				variant="outlined"
				inputProps={{
					inputMode: 'numeric',
					pattern: '[0-9]*',
				}}
				unit={unit}
				defaultValue={defaultValue}
				onChange={onChange}
				onBlur={onBlur}
				min={min}
				max={max}
				step="any"
			/>
		);
	} else {
		// Scientific units that don't need conversion (kW, Hz, m³/s, etc.)
		return (
			<TextField
				{...otherProps}
				type="number"
				fullWidth
				variant="outlined"
				onChange={onChange}
				InputProps={{
					endAdornment: unit ? (
						<InputAdornment position="end">{unit}</InputAdornment>
					) : null,
					...otherProps.InputProps,
				}}
				inputProps={{
					inputMode: 'numeric',
					pattern: '[0-9]*',
				}}
			/>
		);
	}
};

export default UnitAwareField;
